import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Wrench, Car, Users, FileText, BarChart3, Settings as Cog, LogOut, Menu, Gauge, BookOpen, CalendarClock, CalendarDays, ShieldCheck, Inbox, Ticket, Boxes, Wallet, UserCog, Receipt } from "lucide-react";
import OnboardingDialog from "@/components/OnboardingDialog";
import SupportWidget from "@/components/SupportWidget";

function applyTheme() {
  document.documentElement.setAttribute("data-theme", "gold");
}
applyTheme();

export const NAV_ITEMS = [
  { to: "/", label: "Prehľad", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/zakazky", label: "Zákazky", icon: Wrench, id: "jobs" },
  { to: "/sklad", label: "Sklad", icon: Boxes, id: "warehouse" },
  { to: "/planovac", label: "Plánovač", icon: CalendarDays, id: "planner" },
  { to: "/vozidla", label: "Vozidlá", icon: Car, id: "vehicles" },
  { to: "/pripomienky", label: "Pripomienky", icon: CalendarClock, id: "reminders" },
  { to: "/zakaznici", label: "Zákazníci", icon: Users, id: "customers" },
  { to: "/faktury", label: "Faktúry", icon: FileText, id: "invoices" },
  { to: "/interne-doklady", label: "Interné doklady", icon: Receipt, id: "internal-docs" },
  { to: "/financie", label: "Financie", icon: Wallet, id: "finance", adminOnly: true },
  { to: "/zamestnanci", label: "Zamestnanci", icon: UserCog, id: "employees" },
  { to: "/prijate-efaktury", label: "Prijaté e-faktúry", icon: Inbox, id: "inbox" },
  { to: "/statistiky", label: "Štatistiky", icon: BarChart3, id: "stats" },
  { to: "/cennik-prac", label: "Cenník prác", icon: BookOpen, id: "catalog" },
  { to: "/nastavenia", label: "Nastavenia", icon: Cog, id: "settings" },
];

export function orderNav(order) {
  if (!Array.isArray(order) || !order.length) return NAV_ITEMS;
  const byId = Object.fromEntries(NAV_ITEMS.map((n) => [n.id, n]));
  const seen = new Set();
  const out = [];
  order.forEach((id) => { if (byId[id] && !seen.has(id)) { out.push(byId[id]); seen.add(id); } });
  NAV_ITEMS.forEach((n) => { if (!seen.has(n.id)) out.push(n); });
  return out;
}

function readLocalOrder() {
  try { return orderNav(JSON.parse(localStorage.getItem("nav_order") || "null")); }
  catch { return NAV_ITEMS; }
}

function NavItems({ items, onClick }) {
  return items.map((n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.end}
      onClick={onClick}
      data-testid={`nav-${n.id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-base font-medium transition-colors ${
          isActive ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`
      }
    >
      <n.icon className="h-5 w-5 shrink-0" />
      {n.label}
    </NavLink>
  ));
}

const LOW_CREDIT = 5;
function LowCreditBanner() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/billing/me").then((r) => setC(r.data)).catch(() => {}); }, []);
  const threshold = c?.low_credit_threshold ?? LOW_CREDIT;
  if (!c || c.unlimited || c.spz_credits == null || c.spz_credits > threshold) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-500" data-testid="low-credit-banner">
      <Ticket className="h-4 w-4 shrink-0" />
      <span>Máte už len <strong>{c.spz_credits}</strong> {c.spz_credits === 1 ? "kredit" : "kreditov"}. Overenie ŠPZ a AI výpočty sa po minutí zastavia.</span>
      <NavLink to="/nastavenia" className="ml-auto font-semibold underline underline-offset-2 hover:text-amber-400" data-testid="low-credit-topup">Dobiť / predplatné</NavLink>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(readLocalOrder);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (active && Array.isArray(data?.nav_order) && data.nav_order.length) {
          localStorage.setItem("nav_order", JSON.stringify(data.nav_order));
          setItems(orderNav(data.nav_order));
        }
      } catch (e) { console.debug("nav order load failed, keeping local/default", e); }
    })();
    const onChange = () => setItems(readLocalOrder());
    window.addEventListener("nav-order-changed", onChange);
    return () => { active = false; window.removeEventListener("nav-order-changed", onChange); };
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const navItems = items.filter((n) => !n.adminOnly || user?.role === "admin");

  const Brand = (
    <div className="flex items-center gap-2 px-2 py-4">
      <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
        <Gauge className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <div className="font-heading text-lg font-extrabold uppercase tracking-wide">APIS</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Manager</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <OnboardingDialog />
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-border bg-card/60 backdrop-blur-xl lg:flex">
        {Brand}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3">
          <NavItems items={navItems} />
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin-panel" className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-base font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
              <ShieldCheck className="h-5 w-5 shrink-0" /> Administrácia
            </NavLink>
          )}
        </nav>
        <div className="border-t border-border p-3">
          {user?.mode === "demo" && <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-center text-[11px] font-semibold text-amber-400" data-testid="demo-badge">DEMO režim – obmedzený počet záznamov</div>}
          <div className="mb-2 truncate px-2 text-xs text-muted-foreground">{user?.email}</div>
          <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground" onClick={doLogout} data-testid="logout-button">
            <LogOut className="h-5 w-5" /> Odhlásiť sa
          </Button>
          <SupportWidget />
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-6 w-6" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 border-border bg-card p-0">
            {Brand}
            <nav className="flex flex-col gap-1 px-3">
              <NavItems items={navItems} onClick={() => setOpen(false)} />
              {user?.role === "admin" && (
                <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-panel-mobile" className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-base font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                  <ShieldCheck className="h-5 w-5 shrink-0" /> Administrácia
                </NavLink>
              )}
            </nav>
            <div className="mt-4 border-t border-border p-3">
              <Button variant="ghost" className="w-full justify-start gap-3" onClick={doLogout}>
                <LogOut className="h-5 w-5" /> Odhlásiť sa
              </Button>
              <SupportWidget />
            </div>
          </SheetContent>
        </Sheet>
        <div className="font-heading text-lg font-extrabold uppercase tracking-wide">APIS Manager</div>
        <div className="w-10" />
      </header>

      <main className="lg:pl-60">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
          <LowCreditBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
